import {memo, useMemo} from 'react';
import {Col, Row} from 'components/Grid';
import Picture from 'components/Picture';
import {Typography} from 'components/Typography';
import {useBrandsRoute} from 'core/modules/routing/hooks/useBrandsRoute';
import {filterBrandsByCyrillicWords, findBrandsByFirstLetter} from 'modules/brands/helpers';
import {IBrand} from 'modules/brands/models';
import {useGetBrandsListQuery} from 'modules/brands/services';
import {TBrandAlphabetLetter} from 'modules/brands/types';
import style from './style.module.scss';
import {BRANDS_ALL_CODE, BRANDS_CYRILLIC_CODE, BRANDS_TOP_CODE} from 'modules/brands/constants';
import {getMediaSrc} from 'core/helpers/media';
import classNames from 'classnames';
import {getProductBrandUrl} from 'modules/product-card/helpers';
import Link from 'components/CustomRoutingLink';

interface IBrandCatalogProps {
    selectedLetter?: TBrandAlphabetLetter;
}

const BrandCatalog = memo<IBrandCatalogProps>(function ({selectedLetter}) {
    const {label, filteredCode} = selectedLetter ?? {};
    const {url} = useBrandsRoute();

    const {data} = useGetBrandsListQuery();

    const brands = data?.data?.items as IBrand[];

    const list = useMemo(() => {
        switch (filteredCode) {
            case BRANDS_CYRILLIC_CODE:
                return filterBrandsByCyrillicWords(brands);
            case BRANDS_ALL_CODE:
                return brands;
            case BRANDS_TOP_CODE:
                return brands?.filter((item) => item.isTopBrand);
            default:
                return findBrandsByFirstLetter(brands, filteredCode);
        }
    }, [brands, filteredCode]);

    const renderTopBrands = useMemo(
        () =>
            BRANDS_TOP_CODE === filteredCode || !filteredCode ? (
                <div className={style.top}>
                    {list
                        ?.filter((item) => item.isTopBrand)
                        .map(({logo, code}, index) => (
                            <Link className={style.link} href={getProductBrandUrl(code)} key={index}>
                                <div className={style.item}>
                                    <Picture
                                        alt={`${index}`}
                                        className={style.img}
                                        sources={{}}
                                        src={getMediaSrc(logo.url, {isWebp: true, width: 430})}
                                    />
                                </div>
                            </Link>
                        ))}
                </div>
            ) : null,
        [filteredCode, list],
    );

    const renderBrandsList = useMemo(
        () =>
            BRANDS_TOP_CODE !== filteredCode ? (
                <Row>
                    <Col lg={10} md={12} sm={10}>
                        <div
                            className={classNames(style.wordList, {
                                [style.hidden]: !filteredCode,
                            })}
                        >
                            {list?.map(({code, title}) => (
                                <div className={style.wordItem}>
                                    <Link href={url({id: code})}>
                                        <Typography className={style.text} variant={'price-preview-l-reg'}>
                                            {title}
                                        </Typography>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            ) : null,
        [filteredCode, list, url],
    );

    return (
        <div className={style.brandCatalog}>
            <div className={style.header}>
                <Typography variant={'h4-medium'}>{label ?? 'Топ-бренды'}</Typography>
            </div>
            <div>
                {renderTopBrands}
                {renderBrandsList}
            </div>
        </div>
    );
});

export default BrandCatalog;
